import React from 'react';
import { Link } from 'react-router-dom';
import { X, TrendingUp } from 'lucide-react';

function formatResetTime(isoString) {
  if (!isoString) return null;
  try {
    const d = new Date(isoString);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return null;
  }
}

/**
 * Modal shown when the backend returns 402 (quota exceeded) on a profile view.
 *
 * Props:
 *   quotaError  — { quota_type, entity_type, quota_used, quota_limit, resets_at }
 *   onClose     — callback to dismiss
 */
export default function QuotaUpgradeModal({ quotaError, onClose }) {
  if (!quotaError) return null;

  const { quota_type, entity_type, quota_used, quota_limit, resets_at } = quotaError;
  const isDaily = quota_type === 'daily';
  const resetTime = formatResetTime(resets_at);
  const entityLabel = entity_type === 'analyst' ? 'analyst profiles'
    : entity_type === 'prediction' ? 'predictions'
    : entity_type === 'symbol' ? 'symbols'
    : 'profiles';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={18} />
        </button>

        <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-5">
          <TrendingUp className="text-indigo-600" size={24} />
        </div>

        <h2 className="text-xl font-bold text-gray-900 mb-2">
          {isDaily ? 'Daily limit reached' : 'Monthly limit reached'}
        </h2>

        <p className="text-gray-600 mb-1">
          You've viewed{' '}
          <span className="font-semibold text-gray-900">{quota_used}/{quota_limit}</span>{' '}
          {entityLabel} {isDaily ? 'today' : 'this month'}.
        </p>

        {resetTime && (
          <p className="text-sm text-gray-400 mb-6">
            Resets {isDaily ? 'at' : 'on'}: {resetTime}
          </p>
        )}

        <div className="flex flex-col gap-3">
          <Link
            to="/upgrade"
            onClick={onClose}
            className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
          >
            Upgrade to Pro — Unlimited Access
          </Link>
          <button
            onClick={onClose}
            className="w-full px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium text-sm transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
